import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, Plus, Users, UserPlus, Clock, Shield, Pencil, Trash2,
  CheckCircle2, XCircle, LogIn, LogOut as LogOutIcon, Timer, Phone, Mail,
  UtensilsCrossed, HandCoins, Loader2, Percent
} from "lucide-react";
import { format, parseISO, differenceInHours, differenceInMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion } from "framer-motion";

const ROLE_MAP: Record<string, { label: string; color: string }> = {
  manager: { label: "Gerente", color: "bg-purple-500/10 text-purple-600 border-purple-500/30" },
  attendant: { label: "Atendente", color: "bg-blue-500/10 text-blue-600 border-blue-500/30" },
  kitchen: { label: "Cozinha", color: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
  cashier: { label: "Caixa", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" },
};

type Staff = {
  id: string; restaurant_id: string; user_id: string; name: string; email: string;
  phone: string | null; role: string; is_active: boolean; pin_code: string | null;
  notes: string | null; invited_at: string; accepted_at: string | null;
};

type ClockRecord = {
  id: string; staff_id: string; clock_in: string; clock_out: string | null;
  total_hours: number | null; notes: string | null;
};

const Funcionarios = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("team");
  const [staff, setStaff] = useState<Staff[]>([]);
  const [clockRecords, setClockRecords] = useState<ClockRecord[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<any>({});
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  // Waiter state
  const [waiters, setWaiters] = useState<any[]>([]);
  const [waiterDialogOpen, setWaiterDialogOpen] = useState(false);
  const [waiterForm, setWaiterForm] = useState<any>({});
  const [savingWaiter, setSavingWaiter] = useState(false);
  const [serviceFee, setServiceFee] = useState(0);
  const [savingFee, setSavingFee] = useState(false);
  const [restaurant, setRestaurant] = useState<any>(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }
    
    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants").select("id").eq("id", impersonateId).single();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants").select("id").eq("owner_id", user.id).single();
      rest = data;
    }
    
    if (!rest) { navigate("/dashboard"); return; }
    setRestaurantId(rest.id);

    // Get restaurant details for service fee
    const { data: fullRest } = await supabase.from("restaurants").select("*").eq("id", rest.id).single();
    setRestaurant(fullRest);
    setServiceFee((fullRest as any)?.service_fee_percentage || 0);

    const [staffRes, clockRes] = await Promise.all([
      supabase.from("restaurant_staff").select("*").eq("restaurant_id", rest.id).order("name"),
      supabase.from("staff_clock_records").select("*").eq("restaurant_id", rest.id).order("clock_in", { ascending: false }).limit(200),
    ]);

    setStaff((staffRes.data as any[]) || []);
    setClockRecords((clockRes.data as any[]) || []);

    // Load waiters
    const { data: waiterData } = await supabase.from("waiters").select("*").eq("restaurant_id", rest.id).order("name");
    setWaiters(waiterData || []);

    setLoading(false);
  };

  const activeStaff = useMemo(() => staff.filter(s => s.is_active), [staff]);
  const inactiveStaff = useMemo(() => staff.filter(s => !s.is_active), [staff]);

  // Currently clocked-in staff
  const clockedIn = useMemo(() => {
    const openRecords = clockRecords.filter(r => !r.clock_out);
    return openRecords.map(r => ({ record: r, member: staff.find(s => s.id === r.staff_id) })).filter(x => x.member);
  }, [clockRecords, staff]);

  // Hours summary per staff this month
  const monthlyHours = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const map: Record<string, number> = {};
    clockRecords.forEach(r => {
      if (r.total_hours && parseISO(r.clock_in) >= monthStart) {
        map[r.staff_id] = (map[r.staff_id] || 0) + Number(r.total_hours);
      }
    });
    return map;
  }, [clockRecords]);

  const openAddDialog = (editItem?: Staff) => {
    if (editItem) {
      setForm({ ...editItem, editing: true });
    } else {
      setForm({ name: "", email: "", phone: "", role: "attendant", pin_code: "", notes: "", is_active: true });
    }
    setDialogOpen(true);
  };

  const saveStaff = async () => {
    if (!restaurantId || !form.name || !form.email) return;
    try {
      // For new staff, we create a placeholder user_id (the owner will manage them)
      // In a full implementation, this would send an invite email
      const payload: any = {
        restaurant_id: restaurantId,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        role: form.role,
        pin_code: form.pin_code || null,
        notes: form.notes || null,
        is_active: form.is_active,
      };

      if (form.editing) {
        await supabase.from("restaurant_staff").update(payload).eq("id", form.id);
      } else {
        // Use a deterministic UUID based on email for now (staff don't need auth login in v1)
        payload.user_id = crypto.randomUUID();
        await supabase.from("restaurant_staff").insert(payload);
      }
      toast({ title: form.editing ? "Funcionário atualizado!" : "Funcionário adicionado!" });
      setDialogOpen(false);
      loadData();
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };

  const toggleActive = async (s: Staff) => {
    await supabase.from("restaurant_staff").update({ is_active: !s.is_active } as any).eq("id", s.id);
    toast({ title: s.is_active ? "Funcionário desativado" : "Funcionário reativado" });
    loadData();
  };

  const deleteStaff = async (id: string) => {
    await supabase.from("restaurant_staff").delete().eq("id", id);
    toast({ title: "Funcionário removido" });
    loadData();
  };

  // Clock in/out
  const clockIn = async (s: Staff) => {
    if (!restaurantId) return;
    await supabase.from("staff_clock_records").insert({ restaurant_id: restaurantId, staff_id: s.id } as any);
    toast({ title: `${s.name} registrou entrada!` });
    loadData();
  };

  const clockOut = async (recordId: string) => {
    const record = clockRecords.find(r => r.id === recordId);
    if (!record) return;
    const now = new Date();
    const start = parseISO(record.clock_in);
    const hours = differenceInMinutes(now, start) / 60;
    await supabase.from("staff_clock_records").update({ clock_out: now.toISOString(), total_hours: Math.round(hours * 100) / 100 } as any).eq("id", recordId);
    toast({ title: "Saída registrada!" });
    loadData();
  };

  const openClockHistory = (s: Staff) => {
    setSelectedStaff(s);
    setClockDialogOpen(true);
  };

  const staffClockHistory = useMemo(() => {
    if (!selectedStaff) return [];
    return clockRecords.filter(r => r.staff_id === selectedStaff.id);
  }, [selectedStaff, clockRecords]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Carregando...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-card/80 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold">Gestão de Funcionários</h1>
        </div>
        <Badge variant="secondary" className="ml-2">{activeStaff.length} ativos</Badge>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10 text-primary"><Users className="w-5 h-5" /></div>
            <div><p className="text-[11px] text-muted-foreground">Total Equipe</p><p className="text-lg font-bold">{staff.length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-500/10 text-emerald-500"><CheckCircle2 className="w-5 h-5" /></div>
            <div><p className="text-[11px] text-muted-foreground">Ativos</p><p className="text-lg font-bold">{activeStaff.length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-500/10 text-blue-500"><LogIn className="w-5 h-5" /></div>
            <div><p className="text-[11px] text-muted-foreground">Trabalhando Agora</p><p className="text-lg font-bold">{clockedIn.length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-500/10 text-amber-500"><Timer className="w-5 h-5" /></div>
            <div><p className="text-[11px] text-muted-foreground">Cargos</p><p className="text-lg font-bold">{new Set(staff.map(s => s.role)).size}</p></div>
          </CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="team">Equipe</TabsTrigger>
            <TabsTrigger value="waiters">Garçons</TabsTrigger>
            <TabsTrigger value="clock">Ponto</TabsTrigger>
            <TabsTrigger value="hours">Horas</TabsTrigger>
          </TabsList>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">Equipe</h2>
              <Button size="sm" onClick={() => openAddDialog()}><UserPlus className="w-4 h-4 mr-1" /> Adicionar</Button>
            </div>

            <div className="space-y-2">
              {staff.map(s => (
                <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center gap-3 p-3 rounded-lg bg-card border border-border ${!s.is_active ? "opacity-50" : ""}`}>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    {s.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className={`shrink-0 text-[10px] ${ROLE_MAP[s.role]?.color || ""}`}>
                    {ROLE_MAP[s.role]?.label || s.role}
                  </Badge>
                  {clockedIn.some(c => c.member?.id === s.id) && (
                    <Badge className="shrink-0 text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30" variant="outline">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" /> Online
                    </Badge>
                  )}
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openAddDialog(s)}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleActive(s)}>
                      {s.is_active ? <XCircle className="w-3.5 h-3.5 text-muted-foreground" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteStaff(s.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </motion.div>
              ))}
              {staff.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Nenhum funcionário cadastrado</p>}
            </div>
          </TabsContent>

          {/* Clock Tab */}
          <TabsContent value="clock" className="space-y-4 mt-4">
            <h2 className="font-semibold">Registro de Ponto</h2>

            {clockedIn.length > 0 && (
              <Card className="border-emerald-500/30 bg-emerald-500/5">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-600 flex items-center gap-2"><Clock className="w-4 h-4" /> Trabalhando agora</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {clockedIn.map(({ record, member }) => (
                    <div key={record.id} className="flex items-center justify-between bg-background/50 rounded p-2">
                      <div>
                        <p className="text-sm font-medium">{member!.name}</p>
                        <p className="text-xs text-muted-foreground">Entrada: {format(parseISO(record.clock_in), "HH:mm", { locale: ptBR })}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => clockOut(record.id)}>
                        <LogOutIcon className="w-3.5 h-3.5 mr-1" /> Registrar Saída
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Registrar Entrada</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {activeStaff.filter(s => !clockedIn.some(c => c.member?.id === s.id)).map(s => (
                    <Button key={s.id} variant="outline" className="h-auto py-3 flex flex-col items-center gap-1" onClick={() => clockIn(s)}>
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                        {s.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <span className="text-xs font-medium">{s.name.split(" ")[0]}</span>
                      <Badge variant="outline" className="text-[9px] h-4">{ROLE_MAP[s.role]?.label}</Badge>
                    </Button>
                  ))}
                  {activeStaff.filter(s => !clockedIn.some(c => c.member?.id === s.id)).length === 0 && (
                    <p className="text-xs text-muted-foreground col-span-full text-center py-4">Todos os funcionários ativos já registraram entrada</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent records */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Registros Recentes</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Funcionário</TableHead>
                      <TableHead className="text-xs">Entrada</TableHead>
                      <TableHead className="text-xs">Saída</TableHead>
                      <TableHead className="text-xs text-right">Horas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clockRecords.slice(0, 20).map(r => {
                      const member = staff.find(s => s.id === r.staff_id);
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs font-medium">{member?.name || "—"}</TableCell>
                          <TableCell className="text-xs">{format(parseISO(r.clock_in), "dd/MM HH:mm")}</TableCell>
                          <TableCell className="text-xs">{r.clock_out ? format(parseISO(r.clock_out), "dd/MM HH:mm") : <Badge variant="outline" className="text-[9px]">Em andamento</Badge>}</TableCell>
                          <TableCell className="text-xs text-right">{r.total_hours ? `${r.total_hours.toFixed(1)}h` : "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                    {clockRecords.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">Nenhum registro</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hours Summary Tab */}
          <TabsContent value="hours" className="space-y-4 mt-4">
            <h2 className="font-semibold">Resumo de Horas — Mês Atual</h2>
            <div className="space-y-2">
              {activeStaff.map(s => {
                const hours = monthlyHours[s.id] || 0;
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border cursor-pointer hover:bg-accent/50 transition"
                    onClick={() => openClockHistory(s)}>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                      {s.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{s.name}</p>
                      <Badge variant="outline" className={`text-[10px] ${ROLE_MAP[s.role]?.color || ""}`}>{ROLE_MAP[s.role]?.label}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{hours.toFixed(1)}h</p>
                      <p className="text-[10px] text-muted-foreground">este mês</p>
                    </div>
                  </motion.div>
                );
              })}
              {activeStaff.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Nenhum funcionário ativo</p>}
            </div>
          </TabsContent>

          {/* Waiters Tab */}
          <TabsContent value="waiters" className="space-y-4 mt-4">
            {/* Service Fee Config */}
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">Taxa de Serviço</p>
                      <p className="text-xs text-muted-foreground">Aplicada automaticamente no atendimento por garçom</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      step={1}
                      value={serviceFee}
                      onChange={e => setServiceFee(Number(e.target.value))}
                      className="w-20 h-8 text-sm text-center"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={savingFee}
                      onClick={async () => {
                        if (!restaurantId) return;
                        setSavingFee(true);
                        await supabase.from("restaurants").update({ service_fee_percentage: serviceFee } as any).eq("id", restaurantId);
                        toast({ title: "Taxa de serviço atualizada!" });
                        setSavingFee(false);
                      }}
                    >
                      {savingFee ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salvar"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between items-center">
              <h2 className="font-semibold flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4" /> Garçons ({waiters.filter(w => w.is_active).length})
              </h2>
              <Button size="sm" onClick={() => { setWaiterForm({ name: "", email: "", password: "", phone: "", mp_access_token: "", mp_public_key: "" }); setWaiterDialogOpen(true); }}>
                <UserPlus className="w-4 h-4 mr-1" /> Novo Garçom
              </Button>
            </div>

            {waiters.length === 0 && (
              <Card className="border-dashed border-2 border-border">
                <CardContent className="p-6 text-center space-y-2">
                  <UtensilsCrossed className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nenhum garçom cadastrado</p>
                  <p className="text-xs text-muted-foreground">Cadastre garçons para que acessem o PDV móvel em <strong>/garcom/login</strong></p>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {waiters.map(w => (
                <motion.div key={w.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center gap-3 p-3 rounded-lg bg-card border border-border ${!w.is_active ? "opacity-50" : ""}`}>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    <UtensilsCrossed className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{w.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{w.email}</span>
                      {w.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{w.phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {w.mp_access_token ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                        <HandCoins className="w-3 h-3 mr-0.5" /> PIX
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">Sem PIX</Badge>
                    )}
                    <Badge variant={w.is_active ? "default" : "secondary"} className="text-[10px]">
                      {w.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                      setWaiterForm({ ...w, editing: true, password: "" });
                      setWaiterDialogOpen(true);
                    }}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                      await supabase.from("waiters").update({ is_active: !w.is_active }).eq("id", w.id);
                      toast({ title: w.is_active ? "Garçom desativado" : "Garçom reativado" });
                      loadData();
                    }}>
                      {w.is_active ? <XCircle className="w-3.5 h-3.5 text-muted-foreground" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

        </Tabs>
      </main>

      {/* Add/Edit Staff Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{form.editing ? "Editar Funcionário" : "Novo Funcionário"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome completo" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" /></div>
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(00) 00000-0000" /></div>
            <div><Label>Cargo</Label>
              <Select value={form.role || "attendant"} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Gerente</SelectItem>
                  <SelectItem value="attendant">Atendente</SelectItem>
                  <SelectItem value="kitchen">Cozinha</SelectItem>
                  <SelectItem value="cashier">Caixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>PIN de Acesso (4 dígitos)</Label><Input maxLength={4} value={form.pin_code || ""} onChange={e => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, "") })} placeholder="1234" /></div>
            <div><Label>Observações</Label><Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            {form.editing && (
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label>Ativo</Label>
              </div>
            )}
            <Button className="w-full" onClick={saveStaff} disabled={!form.name || !form.email}>
              {form.editing ? "Salvar Alterações" : "Adicionar Funcionário"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clock History Dialog */}
      <Dialog open={clockDialogOpen} onOpenChange={setClockDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Histórico de Ponto — {selectedStaff?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Data</TableHead>
                  <TableHead className="text-xs">Entrada</TableHead>
                  <TableHead className="text-xs">Saída</TableHead>
                  <TableHead className="text-xs text-right">Horas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffClockHistory.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{format(parseISO(r.clock_in), "dd/MM/yyyy")}</TableCell>
                    <TableCell className="text-xs">{format(parseISO(r.clock_in), "HH:mm")}</TableCell>
                    <TableCell className="text-xs">{r.clock_out ? format(parseISO(r.clock_out), "HH:mm") : "—"}</TableCell>
                    <TableCell className="text-xs text-right font-medium">{r.total_hours ? `${r.total_hours.toFixed(1)}h` : "—"}</TableCell>
                  </TableRow>
                ))}
                {staffClockHistory.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">Nenhum registro</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {staffClockHistory.length > 0 && (
            <div className="text-right text-sm font-semibold border-t border-border pt-2">
              Total: {staffClockHistory.reduce((s, r) => s + (r.total_hours || 0), 0).toFixed(1)}h
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Waiter Dialog */}
      <Dialog open={waiterDialogOpen} onOpenChange={setWaiterDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{waiterForm.editing ? "Editar Garçom" : "Novo Garçom"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Nome *</Label>
              <Input value={waiterForm.name || ""} onChange={e => setWaiterForm({ ...waiterForm, name: e.target.value })} placeholder="Nome completo" className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">E-mail *</Label>
              <Input type="email" value={waiterForm.email || ""} onChange={e => setWaiterForm({ ...waiterForm, email: e.target.value })} placeholder="garcom@restaurante.com" className="h-9 text-sm" disabled={waiterForm.editing} />
            </div>
            {!waiterForm.editing && (
              <div className="space-y-1">
                <Label className="text-xs">Senha *</Label>
                <Input type="password" value={waiterForm.password || ""} onChange={e => setWaiterForm({ ...waiterForm, password: e.target.value })} placeholder="Mínimo 6 caracteres" className="h-9 text-sm" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Telefone</Label>
              <Input value={waiterForm.phone || ""} onChange={e => setWaiterForm({ ...waiterForm, phone: e.target.value })} placeholder="(11) 99999-9999" className="h-9 text-sm" />
            </div>
            <div className="border-t border-border pt-3 space-y-1">
              <Label className="text-xs font-semibold flex items-center gap-1"><HandCoins className="w-3 h-3" /> Mercado Pago (Gorjetas)</Label>
              <p className="text-[11px] text-muted-foreground">Credenciais pessoais do garçom para receber gorjetas via PIX</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Access Token</Label>
              <Input value={waiterForm.mp_access_token || ""} onChange={e => setWaiterForm({ ...waiterForm, mp_access_token: e.target.value })} placeholder="APP_USR-..." className="h-9 text-sm font-mono" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Public Key</Label>
              <Input value={waiterForm.mp_public_key || ""} onChange={e => setWaiterForm({ ...waiterForm, mp_public_key: e.target.value })} placeholder="APP_USR-..." className="h-9 text-sm font-mono" />
            </div>

            <Button className="w-full" disabled={savingWaiter || !waiterForm.name || !waiterForm.email || (!waiterForm.editing && (!waiterForm.password || waiterForm.password.length < 6))}
              onClick={async () => {
                setSavingWaiter(true);
                try {
                  if (waiterForm.editing) {
                    // Update existing waiter
                    await supabase.from("waiters").update({
                      name: waiterForm.name,
                      phone: waiterForm.phone || null,
                      mp_access_token: waiterForm.mp_access_token || null,
                      mp_public_key: waiterForm.mp_public_key || null,
                    }).eq("id", waiterForm.id);
                    toast({ title: "Garçom atualizado!" });
                  } else {
                    // Create via edge function
                    const { data: { session } } = await supabase.auth.getSession();
                    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-waiter`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                        "Authorization": `Bearer ${session?.access_token}`,
                      },
                      body: JSON.stringify({
                        name: waiterForm.name,
                        email: waiterForm.email,
                        password: waiterForm.password,
                        phone: waiterForm.phone || null,
                        restaurant_id: restaurantId,
                        mp_access_token: waiterForm.mp_access_token || null,
                        mp_public_key: waiterForm.mp_public_key || null,
                      }),
                    });
                    const result = await response.json();
                    if (!response.ok) throw new Error(result.error || "Erro ao criar garçom");
                    toast({ title: "Garçom criado!", description: `Login: ${waiterForm.email}` });
                  }
                  setWaiterDialogOpen(false);
                  loadData();
                } catch (err: any) {
                  toast({ title: "Erro", description: err.message, variant: "destructive" });
                } finally {
                  setSavingWaiter(false);
                }
              }}
            >
              {savingWaiter && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {waiterForm.editing ? "Salvar Alterações" : "Criar Garçom"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Funcionarios;
